import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { filter, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    shareReplay,
    Subject,
    withLatestFrom,
} from 'rxjs';
import { BazisFormService } from '@bazis/form/services/form.service';
import { EntityFormArray, EntityFormGroup, FormSettings } from '@bazis/form/models/form.types';
import { EntityStatusListItem } from '@bazis/shared/components/entity-status-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BazisConfigurationService,
    FORM_SAVE_BUTTON_SETTINGS,
    SHARE_REPLAY_SETTINGS,
} from '@bazis/configuration.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';

@Component({
    selector: 'bazis-edit-entity-default',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [BazisFormService],
    standalone: false,
})
export class BazisEditEntityDefaultComponent implements OnInit {
    formSettings: FormSettings = {};

    statusToSaveByButton = '';

    statusBlock$: Observable<EntityStatusListItem[]> = combineLatest([
        this.formService.form$,
        this.formService.status$,
    ]).pipe(
        map(() => []),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    forceSave$ = new Subject();

    changes$ = this.formService.haveUnsavedChanges$.pipe(
        withLatestFrom(this.formService.entity$),
        filter(
            ([changes, entity]) =>
                changes &&
                entity &&
                (!this.statusToSaveByButton ||
                    entity.$snapshot.status.id !== this.statusToSaveByButton),
        ),
        switchMap(() => this.formService.save()),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    syncChanges$ = this.formService.syncChanges$.pipe(
        withLatestFrom(this.formService.entity$),
        filter(
            ([canChange, entity]) =>
                canChange &&
                this.statusToSaveByButton &&
                entity &&
                entity.$snapshot.status.id !== this.statusToSaveByButton,
        ),
        switchMap(() => this.formService.save()),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    saveByButton$ = this.forceSave$.pipe(
        filter((v) => !!v),
        tap(() => {
            this.formService.form.updateValueAndValidity();
        }),
        withLatestFrom(this.formService.form$),
        mergeMap(([save, form]) =>
            form.$config.id ? this.formService.haveUnsavedChanges$.pipe(take(1)) : of(true),
        ),
        filter((needSave) => {
            if (!needSave) this.forceSave$.next(false);

            return !!needSave;
        }),
        switchMap(() => this.formService.save()),
        tap((v) => {
            this.forceSave$.next(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    unconditionalSaveByButton$ = this.forceSave$.pipe(
        filter((v) => !!v),
        tap(() => {
            this.formService.form.updateValueAndValidity();
        }),
        withLatestFrom(this.formService.form$),
        switchMap(() => this.formService.save()),
        tap((v) => {
            this.forceSave$.next(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    childrenStatus$: Subject<{ id: string; status: boolean }> = new Subject();

    childrenStatusMap = {};

    haveChildrenStatusChanges$ = this.childrenStatus$.pipe(
        startWith(null),
        map((v) => {
            if (!v) return Object.keys(this.childrenStatusMap).length > 0;

            if (v.status) {
                this.childrenStatusMap[v.id] = true;
            } else {
                delete this.childrenStatusMap[v.id];
            }

            return Object.keys(this.childrenStatusMap).length > 0;
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    hasChanges$ = combineLatest([
        this.haveChildrenStatusChanges$,
        this.formService.haveUnsavedChanges$,
    ]).pipe(
        map(([childrenChanges, ownChanges]) => childrenChanges || ownChanges),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    canTransit$;

    form$ = this.formService.form$.pipe(shareReplay(SHARE_REPLAY_SETTINGS));

    needUpdateEntity = new BehaviorSubject(false);

    updateEntity$;

    saveButtonSettings = FORM_SAVE_BUTTON_SETTINGS;

    forceLoadEntity = true;

    constructor(
        protected router: Router,
        protected route: ActivatedRoute,
        public formService: BazisFormService,
        protected configurationService: BazisConfigurationService,
        protected entityService: BazisEntityService,
    ) {}

    ngOnInit() {
        if (!this.formSettings.id) {
            const id = this.route.snapshot.params.id;
            this.formSettings.id = id === 'new' ? null : id;
        }
        this.formService.setFormSettings(this.formSettings, this.forceLoadEntity);
        this.setCanTransit();

        this.updateEntity$ = this.needUpdateEntity.pipe(
            filter((v) => !!v),
            switchMap((v) =>
                this.entityService.getEntity$(
                    this.formSettings.entityType,
                    this.route.snapshot.params.id,
                    { forceLoad: true },
                ),
            ),
        );
    }

    setCanTransit() {
        this.canTransit$ = combineLatest([
            this.formService.status$,
            this.statusBlock$,
            this.formService.process$,
            this.formService.savingStatus$,
        ]).pipe(
            map(([formStatus, statusBlock, process, savingStatus]) => {
                if (
                    formStatus === 'INVALID' ||
                    !formStatus ||
                    process ||
                    savingStatus?.status === 'process'
                )
                    return false;

                return statusBlock.reduce(
                    (acc, current) =>
                        acc &&
                        (current.isValid !== undefined
                            ? current.isValid
                            : !current.controls.find(
                                  (innerControl) => !this.isValidControl(innerControl),
                              )),
                    true,
                );
            }),
        );
    }

    getArray(form: EntityFormGroup, fieldName: string): EntityFormArray {
        return form.get(fieldName) as EntityFormArray;
    }

    trackByFn(index, item) {
        return item.value || (item.$config ? item.$config.desiredId : item);
    }

    isValidControl(control) {
        return !control || !!control.valid || control.disabled;
    }

    updateSavingStatus(form, fieldName, statusData, index, needUpdateEntity = false) {
        this.formService.setChildSavingStatus(this.getArray(form, fieldName).at(index), statusData);
        if (needUpdateEntity && statusData?.status === 'success') {
            this.needUpdateEntity.next(true);
        }
    }

    getFieldChanges$(field) {
        return this.formService.getFieldChanges$(field);
    }
}
